import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { SchoolSelect, isMaster } from "../components/SchoolSelect";

export default function Exams() {
  const { user } = useAuth();
  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [openExam, setOpenExam] = useState(false);
  const [form, setForm] = useState({
    name: "", exam_type: "daily", class_name: "", section: "", subject: "",
    date: new Date().toISOString().slice(0, 10), max_marks: 100,
  });

  const load = async () => {
    const [e, s] = await Promise.all([api.get("/exams"), api.get("/students")]);
    setExams(e.data); setStudents(s.data);
    if (!selected && e.data[0]) setSelected(e.data[0].id);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!selected) return;
    api.get("/marks", { params: { exam_id: selected } }).then((r) => setMarks(r.data));
  }, [selected]);

  const current = exams.find((e) => e.id === selected);
    const studentsInExam = current
    ? students.filter((s) => s.class_name === current.class_name && s.section === current.section && s.school_id === current.school_id)
    : [];

  const marksMap = useMemo(() => {
    const m = {}; marks.forEach((r) => (m[r.student_id] = r.marks)); return m;
  }, [marks]);

  const createExam = async (e) => {
    e.preventDefault();
    try {
      await api.post("/exams", { ...form, max_marks: Number(form.max_marks) });
      toast.success("Exam created"); setOpenExam(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setMark = async (student_id, value) => {
    if (value === "" || value == null) return;
    try {
      await api.post("/marks", { exam_id: selected, student_id, marks: Number(value) });
      const r = await api.get("/marks", { params: { exam_id: selected } });
      setMarks(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canManage = ["admin", "principal", "teacher"].includes(user.role);
  const avg = marks.length ? (marks.reduce((s, m) => s + m.marks, 0) / marks.length).toFixed(1) : "—";
  const high = marks.length ? Math.max(...marks.map((m) => m.marks)) : "—";
  const low = marks.length ? Math.min(...marks.map((m) => m.marks)) : "—";

  return (
    <div className="space-y-6" data-testid="exams-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black">Exams & Marks</h1>
          <p className="text-zinc-500 mt-1">Daily tests, slip tests, FA/SA — recorded in one place.</p>
        </div>
        {canManage && (
          <Dialog open={openExam} onOpenChange={setOpenExam}>
            <DialogTrigger asChild><Button data-testid="add-exam-btn" className="bg-indigo-700 hover:bg-indigo-800 text-white"><Plus size={16} className="mr-1" />New Exam</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Exam</DialogTitle></DialogHeader>
              <form onSubmit={createExam} className="space-y-3">
                <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="exam-name-input" /></div>
                {isMaster(user) && <SchoolSelect value={form.school_id} onChange={(v) => setForm({ ...form, school_id: v })} testId="exam-school-select" />}
                <div>
                  <Label>Type</Label>
                  <Select value={form.exam_type} onValueChange={(v) => setForm({ ...form, exam_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily Test</SelectItem>
                      <SelectItem value="weekly">Weekly Test</SelectItem>
                      <SelectItem value="slip">Slip Test</SelectItem>
                      <SelectItem value="FA">FA (Formative)</SelectItem>
                      <SelectItem value="SA">SA (Summative)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Class</Label><Input required value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })} /></div>
                  <div><Label>Section</Label><Input required value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} /></div>
                  <div><Label>Subject</Label><Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Date</Label><Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                  <div><Label>Max Marks</Label><Input type="number" required value={form.max_marks} onChange={(e) => setForm({ ...form, max_marks: e.target.value })} /></div>
                </div>
                <Button type="submit" className="w-full bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="exam-submit-btn">Create</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="p-4 border-zinc-200">
        <Label className="text-xs">Select exam</Label>
        <Select value={selected || ""} onValueChange={setSelected}>
          <SelectTrigger data-testid="exam-select" className="w-full md:w-96 mt-1"><SelectValue placeholder="Pick an exam" /></SelectTrigger>
          <SelectContent>
            {exams.map((e) => (
              <SelectItem key={e.id} value={e.id}>{e.name} · {e.subject} · {e.class_name}-{e.section} · {e.date}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Card>

      {current && (
        <>
          <div className="grid grid-cols-4 gap-4">
            <StatBox label="Students" value={studentsInExam.length} />
            <StatBox label="Submitted" value={marks.length} />
            <StatBox label="Class average" value={avg} />
            <StatBox label="High / Low" value={`${high} / ${low}`} />
          </div>

          <Card className="border-zinc-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Roll</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Marks (of {current.max_marks})</TableHead>
                  <TableHead>%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {studentsInExam.map((s) => {
                  const m = marksMap[s.id];
                  const pct = m != null ? ((m / current.max_marks) * 100).toFixed(1) : null;
                  return (
                    <TableRow key={s.id} className="row-hover">
                      <TableCell>{s.roll_no}</TableCell>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell>
                        <Input
                          type="number" min={0} max={current.max_marks}
                          disabled={!canManage}
                          defaultValue={m ?? ""}
                          onBlur={(e) => setMark(s.id, e.target.value)}
                          data-testid={`mark-input-${s.id}`}
                          className="w-28"
                        />
                      </TableCell>
                      <TableCell className="text-zinc-600">{pct != null ? `${pct}%` : "—"}</TableCell>
                    </TableRow>
                  );
                })}
                {studentsInExam.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center py-8 text-zinc-500">No students for this class.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <Card className="p-5 border-zinc-200">
      <div className="text-xs text-zinc-500 uppercase tracking-wide">{label}</div>
      <div className="font-display text-2xl font-bold mt-2">{value}</div>
    </Card>
  );
}
